import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	Input,
} from "@monbits/ui";
import { ArrowLeft, Check } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { useMount } from "react-use";
import { toast } from "sonner";
import { Logo } from "src/components/logo";
import authProvider from "src/providers/auth-provider";

const ForgotPasswordPage: React.FC = () => {
	const emailId = React.useId();

	const [email, setEmail] = React.useState("");
	const [emailError, setEmailError] = React.useState("");
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [isSuccess, setIsSuccess] = React.useState(false);

	useMount(async () => {
		await authProvider.getCookie();
	});

	const validateEmail = (value: string): boolean => {
		if (!value) {
			setEmailError("Please enter your email");
			return false;
		}
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(value)) {
			setEmailError("Please enter a valid email address");
			return false;
		}
		setEmailError("");
		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const isEmailValid = validateEmail(email);

		if (!isEmailValid) {
			return;
		}

		try {
			setIsSubmitting(true);
			await authProvider.initiatePasswordReset(email);
			setIsSuccess(true);
			toast.success("Check your email", {
				description:
					"If an account exists, you'll receive a password reset link",
				icon: <Check className="h-4 w-4" />,
			});
		} catch (error) {
			console.error("Password reset error:", error);
			setIsSuccess(true);
			toast.success("Check your email", {
				description:
					"If an account exists, you'll receive a password reset link",
				icon: <Check className="h-4 w-4" />,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="w-full max-w-md">
				<div className="flex justify-center mb-8">
					<Logo variant="full" size="xl" />
				</div>

				<Card>
					<CardHeader>
						<CardTitle>Reset your password</CardTitle>
						<CardDescription>
							{isSuccess
								? "We've sent you a password reset link"
								: "Enter your email address and we'll send you a link to reset your password"}
						</CardDescription>
					</CardHeader>
					<CardContent>
						{isSuccess ? (
							<div className="space-y-4">
								<div className="rounded-lg bg-(--success-bg) border border-(--success-border) p-4 text-sm text-(--success-fg)">
									<p className="font-medium mb-1">Check your email</p>
									<p>
										If an account exists with <strong>{email}</strong>, you'll
										receive a password reset link shortly.
									</p>
								</div>
								<div className="text-sm text-muted-foreground">
									<p>Didn't receive an email?</p>
									<ul className="list-disc list-inside mt-2 space-y-1">
										<li>Check your spam folder</li>
										<li>Make sure you entered the correct email</li>
										<li>Wait a few minutes and try again</li>
									</ul>
								</div>
								<Link to="/login">
									<Button variant="outline" className="w-full">
										<ArrowLeft className="mr-2 h-4 w-4" />
										Back to login
									</Button>
								</Link>
							</div>
						) : (
							<form onSubmit={handleSubmit}>
								<FieldGroup>
									<Field>
										<FieldLabel htmlFor={emailId}>Email</FieldLabel>
										<Input
											id={emailId}
											type="email"
											placeholder="m@example.com"
											value={email}
											onChange={(e) => {
												setEmail(e.target.value);
												if (emailError) validateEmail(e.target.value);
											}}
											onBlur={() => validateEmail(email)}
											className={emailError ? "border-destructive" : ""}
											required
										/>
										{emailError && (
											<FieldDescription className="text-destructive">
												{emailError}
											</FieldDescription>
										)}
									</Field>

									<Button
										type="submit"
										disabled={isSubmitting}
										className="w-full"
									>
										{isSubmitting ? (
											<span className="flex items-center gap-2">
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
												Sending reset link...
											</span>
										) : (
											"Send reset link"
										)}
									</Button>

									<div className="text-center">
										<Link
											to="/login"
											className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
										>
											<ArrowLeft className="inline mr-1 h-3 w-3" />
											Back to login
										</Link>
									</div>
								</FieldGroup>
							</form>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default ForgotPasswordPage;
